import { CropPhenologyProxy, DeleteRotationPhenologicalStageCommand, GroupedPlantationViewModel, PhenologicalStageItemViewModel, RotationPhenologicalStagesViewModel, RotationPhenologicalStagesViewModelResponseResult } from '@/app/proxy/proxies';
import { SharedModule } from '@/app/shared/shared.module';
import { Component, inject, Input, OnChanges, SimpleChanges } from '@angular/core';
import { DialogService } from 'primeng/dynamicdialog';
import { finalize } from 'rxjs';
import { AddNextStageModalComponent } from './add-next-stage-modal/add-next-stage-modal.component';
import { TranslateService } from '@ngx-translate/core';
import { WeatherSoilPestErrorContainerComponent } from '@/app/features/services/weather-soil-pest/shared/weather-soil-pest-error-container/weather-soil-pest-error-container.component';
import { WeatherSoilPestNodataContainerComponent } from '@/app/features/services/weather-soil-pest/shared/weather-soil-pest-nodata-container/weather-soil-pest-nodata-container.component';
import { ActivityDeleteConfirmModalComponent } from '@/app/features/activities/components/activities-list/activity-delete-confirm-modal/activity-delete-confirm-modal.component';
import { ToastService } from '@/app/core/services/toast.service';
import { MixpanelTrackerService } from '@/app/core/analytics';
import { PlantationService } from '@/app/core/services/plantation.service';

@Component({
  selector: 'app-phenological-stages',
  imports: [SharedModule, WeatherSoilPestNodataContainerComponent, WeatherSoilPestErrorContainerComponent],
  templateUrl: './phenological-stages.component.html',
  styleUrl: './phenological-stages.component.less',
  providers: [CropPhenologyProxy]
})
export class PhenologicalStagesComponent implements OnChanges {
  private cropPhenologyProxy = inject(CropPhenologyProxy);
  private translateService = inject(TranslateService);
  private toastService = inject(ToastService);
  private mixpanelTracker = inject(MixpanelTrackerService);
  private plantationService = inject(PlantationService);
  dialogService = inject(DialogService);

  @Input() selectedPlantation: GroupedPlantationViewModel;
  currentStage: PhenologicalStageItemViewModel;
  currentStageIndex = 0;
  nextStageIndex = 0;
  currentYear: number;

  phenologicalStagesResponse: RotationPhenologicalStagesViewModel;
  loading = true;
  wasThereAnError = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.['selectedPlantation']) {
      this.phenologicalStagesResponse = null;
      this.currentYear = undefined;
      this.currentStageIndex = 0;
      this.currentStage = null;
      this.nextStageIndex = 0;
      this.wasThereAnError = false;

      if (this.selectedPlantation?.activeRotationId) {
        this.getPhenologicalStages();
      }
    }
  }

  getPhenologicalStages() {
    this.phenologicalStagesResponse = null;
    this.currentStage = null;
    this.currentStageIndex = 0;
    this.nextStageIndex = 0;
    this.wasThereAnError = false;
    
    this.loading = true;
    this.cropPhenologyProxy.cropPhenology_GetRotationPhenologicalStages(this.selectedPlantation?.activeRotationId, this.currentYear ?? undefined)
      .pipe(
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe((res: RotationPhenologicalStagesViewModelResponseResult) => {
        if (res.isSuccess) {
          this.phenologicalStagesResponse = res.result;
          this.normalizeStageDates(this.phenologicalStagesResponse.stages);
          if (this.phenologicalStagesResponse.stages?.length > 0) {
            this.currentYear = this.phenologicalStagesResponse.currentYear;

            if (this.phenologicalStagesResponse.currentStageId) {
              this.currentStageIndex = this.phenologicalStagesResponse.stages.findIndex(stage => stage.stageId === this.phenologicalStagesResponse.currentStageId);
              this.currentStage = this.phenologicalStagesResponse.stages[this.currentStageIndex];

              this.nextStageIndex = this.currentStageIndex + 1;
            } else {
              this.nextStageIndex = 0;
            }
          }
        } else {
          this.wasThereAnError = true;
        }
      });
  }

  private normalizeStageDates(stages: PhenologicalStageItemViewModel[]) {
    if (!stages) return;

    stages.forEach(stage => {
      if (!stage.stageDate) return;
      stage.stageDate = stage.stageDate.setZone('utc', { keepLocalTime: true }).startOf('day');
    });
  }

  onChangeCurrentYear(year: number) {
    if (this.currentYear !== year) {
      this.currentStage = null;
      this.currentStageIndex = 0;
      this.currentYear = year;
      this.getPhenologicalStages();
    }
  }

  addNextStage() {
    const modalRef = this.dialogService.open(AddNextStageModalComponent, {
      modal: true,
      closable: true,
      header: this.translateService.instant('doktar_app.services.crop_monitoring.phenology.add_next_stage.title'),
      width: '400px',
      inputValues: {
        sowingStartDate: this.phenologicalStagesResponse.sowingStartDate,
        harvestEndDate:this.phenologicalStagesResponse.harvestEndDate,
        currentStage: this.currentStage,
        nextStage: this.phenologicalStagesResponse.stages[this.nextStageIndex],
        selectedPlantation: this.selectedPlantation
      }
    });

    modalRef.onClose.subscribe(res => {
      if (res) {
        this.getPhenologicalStages();
      }
    })
  }

  updateStage(stageIndex: number) {
    const modalRef = this.dialogService.open(AddNextStageModalComponent, {
      modal: true,
      closable: true,
      header: this.translateService.instant('Edit stage'),
      width: '400px',
      inputValues: {
        mode: 'update',
        sowingStartDate: this.phenologicalStagesResponse.sowingStartDate,
        harvestEndDate:this.phenologicalStagesResponse.harvestEndDate,
        stageIndex,
        stages: this.phenologicalStagesResponse.stages,
        selectedPlantation: this.selectedPlantation
      }
    });

    modalRef.onClose.subscribe(res => {
      if (res) {
        this.getPhenologicalStages();
      }
    })
  }

  actionLoading = false;
  deleteStage(stage: PhenologicalStageItemViewModel) {
    const modalRef = this.dialogService.open(ActivityDeleteConfirmModalComponent, {
      modal: true,
      closable: false,
      width: '450px',
      showHeader: false,
      inputValues: {
        title: 'Reset stage date',
        icon: 'ph-fill ph-eraser',
        description: 'are you sure to reset stage date?',
        confirmBtnLabel: 'doktar_app.common.confirm_button',
        cancelBtnLabel: 'doktar_app.common.cancel_button',
      }
    });

    modalRef.onClose.subscribe((confirmRes) => {
      if (confirmRes) {
        this.actionLoading = true;
        const payload = new DeleteRotationPhenologicalStageCommand();
        payload.rotationId = this.selectedPlantation.activeRotationId;
        payload.stageId = stage.stageId;
        this.cropPhenologyProxy.cropPhenology_DeleteRotationPhenologicalStage(payload)
          .pipe(
            finalize(() => {
              setTimeout(() => {
                this.actionLoading = false;
              }, 500);
            })
          )
          .subscribe({
            next: res => {
              if (res.isSuccess) {
                this.toastService.success();
                this.trackServicesPhenologicalStageReset(stage);
                this.getPhenologicalStages();
              }
            }
          })
      }
    })
  }

  private trackServicesPhenologicalStageReset(stage: PhenologicalStageItemViewModel): void {
    const land = this.plantationService.selectedLand;
    const enterpriseId = land?.enterpriseId || this.plantationService.enterpriseId;
    const enterprise = this.plantationService.originalResult?.find((item) => item.id === enterpriseId);

    this.mixpanelTracker.trackServicesPhenologicalStageReset({
      year: this.currentYear ?? new Date().getFullYear(),
      crop: this.selectedPlantation?.crop?.value ?? '-',
      stage: stage.stageName ?? '-',
      'plantation id': this.selectedPlantation.id,
      'plantation name': this.selectedPlantation?.name,
      'land id': land?.id,
      'land name': land?.name,
      'enterprise id': enterpriseId,
      'enterprise name': enterprise?.enterpriseName,
    });
  }
}